import React, { useState } from 'react';
import { actions, overlays } from '@ecosystem/newron-design-system';
import {
  buildNamespace,
  useCategoryLeaderboardGroupState,
  useFeatureLeaderboardGroupState,
} from '@utils/hook/userPreferences/UseGroupState';
import {
  EXPLORE_LEADERBOARD_TABLE,
  GroupContext,
  Preference,
  PreferenceResponseStatus,
  UNKNOWN_ERROR_MESSAGE,
} from '@utils/userPreferencesUtils/Types';
import { useCategoryOrSegmentsLeaderboardGroupEventState } from '../../utils/CategoryOrSegmentsLeaderboardGroupContext';
import { authentication, useAnalytics } from '@ecosystem/newron-client-lib';
import { useSelector } from 'react-redux';
import {
  CATEGORY_GROUP_TABLE_ELEMENT_CLICKED_TITLE,
  groupElementClickedTrackingFunction,
  TableElementClickedParam,
} from '@utils/page-analytics/model/GroupElementClickTracking';
import { PageName, PageType } from '@utils/pageConfig/Types';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';

export interface Props {
  context: GroupContext;
}

/**
 * @return Modal
 *
 * @remarks
 * a pop-up modal to let user confirm or cancel the deletion of group
 *
 * @param context   - contains retailer client and country
 **/
export const DeleteCategoryOrSegmentsLeaderboardModalGroup: React.FC<Props> = ({ context }) => {
  const {
    deleteCategoryLeaderboardGroup,
    getCategoryLeaderboardGroups,
    deletePreferencePermissionCategoryLeaderboardGroup,
  } = useCategoryLeaderboardGroupState(context);
  const {
    deleteFeatureLeaderboardGroup,
    getFeatureLeaderboardGroup,
    deletePreferencePermissionFeatureLeaderboardGroup,
  } = useFeatureLeaderboardGroupState(context);
  const {
    groupNameToBeDelete,
    setGroupNameToBeDelete,
    isDeleteModalGroupPopUp,
    setIsDeleteModalGroupPopUp,
    currentSelectedFeature,
  } = useCategoryOrSegmentsLeaderboardGroupEventState();
  const pagesConfig = usePageConfig();
  const { track } = useAnalytics();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);

  const [error, setError] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const handleCancel = () => {
    setError('');
    setIsDeleting(false);
    setIsDeleteModalGroupPopUp(false);
    setGroupNameToBeDelete('');
  };

  const getFeatureSpecificQuery = (type: 'delete' | 'get' | 'deletePermission') => {
    const isCategoryFeature = currentSelectedFeature === null || currentSelectedFeature.feature === 'Category';

    const preferenceQueryMap = {
      delete: isCategoryFeature ? deleteCategoryLeaderboardGroup : deleteFeatureLeaderboardGroup,
      get: isCategoryFeature ? getCategoryLeaderboardGroups : getFeatureLeaderboardGroup,
      deletePermission: isCategoryFeature
        ? deletePreferencePermissionCategoryLeaderboardGroup
        : deletePreferencePermissionFeatureLeaderboardGroup,
    };

    return preferenceQueryMap[type];
  };

  const constructDeleteGroupQuery = () => {
    const namespace = buildNamespace(context);
    const userId = authentication.getUser()?.userId ?? '';

    const deleteGroupQuery = getFeatureSpecificQuery('delete');
    const getGroupQuery = getFeatureSpecificQuery('get');
    const deletePermissionQuery = getFeatureSpecificQuery('deletePermission');

    // Retrieve current preference
    const data = getGroupQuery.data as Preference<any>[];
    const currentPreference = data.find(
      (preference) => preference.namespace === namespace && preference.key === groupNameToBeDelete,
    );

    // Reusable mutation handler
    const handleMutation = (query: any, variables: any, onSuccessCallback: () => void) => {
      query.mutate(variables, {
        onSuccess: (response: { status: PreferenceResponseStatus; message: React.SetStateAction<string> }) => {
          setIsDeleting(false);
          if (response.status === PreferenceResponseStatus.SUCCESS) {
            onSuccessCallback();
          } else {
            setError(response.message);
          }
        },
        onError: () => {
          setIsDeleting(false);
          setError(UNKNOWN_ERROR_MESSAGE);
        },
      });
    };

    // If the user is the owner, delete the group
    if (currentPreference?.isPreferenceOwner) {
      handleMutation(deleteGroupQuery, { key: groupNameToBeDelete }, () => handleCancel());
      return;
    }

    // Delete preference permission regardless of ownership
    handleMutation(deletePermissionQuery, { key: groupNameToBeDelete, userId }, () => handleCancel());
  };

  const handleDeleteGroup = () => {
    //Amplitude Tracking when Delete group button is click
    const trackingParam: TableElementClickedParam = {
      eventTitle: CATEGORY_GROUP_TABLE_ELEMENT_CLICKED_TITLE,
      elementName: `Delete ${currentSelectedFeature.feature} Group`,
      elementType: `Table ${currentSelectedFeature.feature} Group`,
      track: track,
      optionChosen: groupNameToBeDelete,
      pageName: PageName.EXPLORE,
      pageType: PageType.EXPLORE,
      sideFilters: sideFiltersState.appliedFilters,
      access: pagesConfig.access,
      tableName: EXPLORE_LEADERBOARD_TABLE,
    };

    groupElementClickedTrackingFunction(trackingParam);

    setError('');
    setIsDeleting(true);

    constructDeleteGroupQuery();
  };
  const { Modal } = overlays;

  const { Button } = actions;
  return (
    <Modal isOpen={isDeleteModalGroupPopUp} onClose={handleCancel}>
      <Modal.Header>
        <h2>Delete Category Leaderboard Group</h2>
      </Modal.Header>
      <Modal.Body>
        <p>Are you sure you want to delete “{groupNameToBeDelete}” group?</p>
        {error && <span className="Input__error">{error}</span>}
      </Modal.Body>
      <Modal.Footer>
        <Button mode="secondary" disabled={isDeleting} onClick={handleCancel}>
          Cancel
        </Button>
        <Button onClick={handleDeleteGroup} disabled={isDeleting}>
          {isDeleting ? 'Deleting...' : 'Yes, delete group'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
